import { mixpanel } from '../../mixpanel'
import { LoginResult, PasswordLoginResult, Role, User } from 'common-billing-server/types'
import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'
import { GenericHttpError, HttpStatusCode } from 'common/http/httpError'
import { INITIAL_PATH, Path } from '../../routes/path'
import { useBillingApi } from '../../api/billing/billing-context'
import { LocalStorageKey, useLocalStorage } from '../../local-storage'
import { cameFrom, NavigationState } from '../common/hooks/hook-stateful-navigate'
import { isRoleAuthorized } from 'common-billing-server/role'
import { authenticateWithPasskey } from 'common-ui/passkeys'

interface AuthContextType {
    user?: User
    refreshUser: () => Promise<void>
    login: (username: string, password: string) => Promise<PasswordLoginResult>
    completeTotpLogin: (challengeId: string, code: string) => Promise<LoginResult>
    completeTotpEnrollment: (enrollmentId: string, code: string) => Promise<LoginResult>
    loginWithPasskey: () => Promise<void>
    logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType)

export function AuthProvider({ children }: { children: ReactNode }) {
    const billingApi = useBillingApi()
    const localStorage = useLocalStorage()
    const [user, setUser] = useState(localStorage.load<User>(LocalStorageKey.user))

    useEffect(() => {
        billingApi.onRequestErrorFn = (httpError: GenericHttpError) => {
            if (httpError.status === HttpStatusCode.Unauthorized) {
                doRemovePersistedUser()
            }
        }
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const login = async (username: string, password: string) => {
        const result = await billingApi.login({ username, password })
        if (result.state === 'authenticated') {
            doPersistUser(result.user)
            mixpanel.trackLogin('password')
        }
        return result
    }

    const completeTotpLogin = async (challengeId: string, code: string) => {
        const result = await billingApi.completeTotpLogins({ challengeId, code })
        doPersistUser(result.user)
        mixpanel.trackLogin('password')
        return result
    }

    const completeTotpEnrollment = async (enrollmentId: string, code: string) => {
        const result = await billingApi.setTotpLoginEnrollment({ enrollmentId, code })
        doPersistUser(result.user)
        mixpanel.trackLogin('password')
        return result
    }

    const loginWithPasskey = async () => {
        const result = await authenticateWithPasskey(
            () => billingApi.getPasskeyLoginOptions(),
            (body) => billingApi.completePasskeyLogins(body),
            (ceremonyId) => billingApi.deletePasskeyLoginOptions(ceremonyId)
        )
        doPersistUser(result.user)
        mixpanel.trackLogin('passkey')
    }

    const refreshUser = async () => {
        const user = await billingApi.getCurrentUser()
        doPersistUser(user)
    }

    const logout = async () => {
        mixpanel.trackClickComponent({ component: 'Auth', action: 'logout' })
        doRemovePersistedUser()
        await billingApi.logout()
    }

    const doPersistUser = (user: User) => {
        mixpanel.setRole(user.role)
        setUser(user)
        localStorage.persist(LocalStorageKey.user, user)
    }

    const doRemovePersistedUser = () => {
        mixpanel.reset()
        localStorage.remove(LocalStorageKey.user)
        setUser(undefined)
    }

    const value: AuthContextType = {
        user,
        refreshUser,
        login,
        completeTotpLogin,
        completeTotpEnrollment,
        loginWithPasskey,
        logout,
    }
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
    return useContext(AuthContext)
}

// Navigates to the login page if the user role is unauthenticated. Else navigates to the received child.
export function RequireAuth({ children }: { children: React.ReactElement }) {
    const auth: AuthContextType = useAuth()
    const location = useLocation()
    if (!auth.user) {
        const navigationState: NavigationState = { from: location }
        return <Navigate to={Path.login} state={navigationState} replace />
    }
    return children
}

// Navigates back to where the user came from if the user role is unauthorized. Else navigates to the received child.
export function RequireRole({
    minimumAllowedRole,
    children,
}: {
    minimumAllowedRole: Role
    children: React.ReactElement
}) {
    const { user } = useAuth()
    const location = useLocation()
    if (!user || !isRoleAuthorized({ role: user.role, minimumAllowedRole })) {
        const navigationState: NavigationState = { from: location }
        const destination = cameFrom(location) || INITIAL_PATH
        return <Navigate to={destination} state={navigationState} replace />
    }
    return children
}
